'use client'

import React, {useContext, useEffect, useMemo, useRef} from 'react'
import {observer} from 'mobx-react'
import {getInjection} from '@runlab/di'
import {useStore} from '@/src/shared/hooks/useStore'
import type {CatalogProductListContent} from '@/src/core/domain/entities/catalog'
import type {FiltersViewModel} from '@/src/features/catalog/filters-v2'
import {isWeb} from '@/src/shared/utils/functions'
import {action, makeObservable, observable} from 'mobx'
import {useMediaQuery} from '@/src/shared/hooks/useMediaQuery'
import theme from '@/src/config/theme'

const Context = React.createContext<FiltersViewModel | null>(null)

export const CsrFiltersProvider = observer(({children}: React.PropsWithChildren) => {
  const {pageData} = useStore<CatalogProductListContent>()
  const store = useMemo(() => getInjection('FiltersViewModelFactory')(pageData!.content.filter), [])
  const initialized = useRef(false)

  const rawFilters = pageData!.content.filter
  const subsections = pageData!.content.subsections ?? []

  useEffect(() => {
    if (isWeb()) {
      if (initialized.current) return
    }
    store.initialize(rawFilters, subsections)
    initialized.current = true
  }, [rawFilters, subsections])

  return <Context.Provider value={store}>{children}</Context.Provider>
})

export const useFilters = () => {
  const store = useContext(Context)

  if (!store) throw new Error('useFilters must be wrapped CsrFiltersProvider')

  return store
}

export const MobileFiltersVisibilityContext =
  React.createContext<Nullable<MobileFiltersVisibility>>(null)

export class MobileFiltersVisibility {
  @observable
  showFilters = false

  constructor() {
    makeObservable(this)
  }

  @action.bound
  toggleVisibility() {
    this.showFilters = !this.showFilters
  }

  @action.bound
  setVisibility(visible: boolean) {
    this.showFilters = visible
  }
}

export const useCatalogFiltersVisibility = () => {
  const store = useContext(MobileFiltersVisibilityContext)

  const isMobile = useMediaQuery(`(max-width: ${theme.mediaSizes.md}px)`)

  useEffect(() => {
    if (store) {
      store.setVisibility(isMobile)
    }
  }, [isMobile, store])

  if (!store) return null

  return {
    visible: store.showFilters,
    toggle: store.toggleVisibility,
  }
}
